// Content for the lightweight footer/info pages. Each is rendered by InfoPage
// using the site's dark theme. Keyed by URL slug.
const p = (title, desc) => ({ title, desc });

// Where we post roles. When empty, the Careers "See openings on LinkedIn"
// button is hidden automatically — set this to the LinkedIn Jobs/company URL
// to turn it on (single source of truth: whatever is posted there shows here).
export const LINKEDIN_JOBS_URL = 'https://www.linkedin.com/company/proven-infosec-pvt-ltd';

export const infoPages = {
  'how-it-works': {
    eyebrow: 'Product',
    title: 'How It Works',
    description:
      'From the first conversation to continuous protection — here is how a partnership with Proven InfoSec works.',
    points: [
      p('Discovery & Scoping', 'We map your assets, risks, and goals to define the right engagement.'),
      p('Assessment & Testing', 'Expert-led testing and analysis surface what matters most.'),
      p('Remediation & Hardening', 'We help you close gaps and strengthen your defenses.'),
      p('Continuous Monitoring', 'Ongoing protection and reporting keep you secure over time.'),
    ],
  },
  integrations: {
    eyebrow: 'How We Work',
    title: 'Works With Your Existing Stack',
    description:
      'We deliver our services alongside the tools your teams already run — SIEM, cloud, CI/CD, and collaboration platforms — without asking you to rip and replace anything.',
    points: [
      p('SIEM & SOAR', 'Our SOC analysts monitor and triage directly within your existing security stack.'),
      p('Cloud Platforms', 'Assessments and hardening across AWS, Azure, and Google Cloud environments.'),
      p('CI/CD Pipelines', 'Security gates added into GitHub Actions, GitLab CI, and other pipelines you already use.'),
      p('Ticketing & Comms', 'Alerts and updates routed through the ticketing and messaging tools your team already uses, like Jira, Slack, and Teams.'),
    ],
  },
  'privacy-policy': {
    eyebrow: 'Legal',
    title: 'Privacy Policy',
    description:
      'How Proven InfoSec collects, uses, and protects your personal data — handled with the same rigor as our security work.',
    points: [
      p('Data We Collect', 'Only what we need to deliver our services.'),
      p('How We Use It', 'Transparent, lawful, and purpose-limited.'),
      p('Your Rights', 'Access, correct, and erase your data on request.'),
      p('Contact Our DPO', 'Reach our Data Protection Officer any time.'),
    ],
  },
  'trinex-exposure-360': {
    eyebrow: 'Product',
    title: 'Trinex Exposure 360',
    description:
      'See your organisation the way an attacker does. Continuous, agentless monitoring of your external attack surface, the dark web, and your third parties — rolled up into a clear, board-ready risk score and grade. Deployments in India and the UK keep monitoring data in the right jurisdiction.',
    points: [
      p('External Attack Surface', 'Continuously map domains, subdomains, exposed services, SSL/DNS posture, and CVEs prioritised by KEV and EPSS — nothing to install, no network access required.'),
      p('Dark Web & Ransomware Monitoring', 'Watch leak sites, ransomware blogs, and threat-actor channels for mentions of your brand, domains, and leaked data — before they become an incident.'),
      p('Credential & Secret Leaks', 'Detect breached employee accounts and exposed secrets across breach datasets, paste sites, and public code repositories.'),
      p('Third-Party Risk & Board Reporting', 'Score your vendors outside-in with clear A–F grades, and roll every finding into an executive risk score your board can actually act on.'),
    ],
  },
  'trinex360-learn': {
    eyebrow: 'Product',
    title: 'Trinex360 Learn',
    description:
      'Enterprise security awareness training that builds real habits — scenario-led courses, gamified simulations, and progress visibility across your whole organization.',
    points: [
      p('Scenario-Led Training', 'Interactive video modules and quizzes covering phishing, CEO fraud, insider threats, and emerging AI and shadow-IT risks.'),
      p('Gamified Simulations', 'Practice simulations and cyber games that measurably improve phishing, fraud, and incident response readiness.'),
      p('Automatic Certification', 'Certificates issued on completion with automated retraining — compliance evidence generated as your team learns.'),
      p('Multi-Tenant Management', 'Role-based access, SSO integration, and per-department rollout built for CISOs, IT, HR, and compliance teams.'),
    ],
  },
  'cookie-policy': {
    eyebrow: 'Legal',
    title: 'Cookie Policy',
    description:
      'How Proven InfoSec uses cookies and similar technologies on this website.',
    points: [
      p('What Cookies Are', 'Small files that help the site work and remember your preferences.'),
      p('Essential Cookies Only', 'We use only the cookies required for core functionality — keeping the site secure and remembering choices like your region. They are never used for tracking or advertising.'),
      p('No Analytics or Advertising', 'This site does not run analytics, advertising, or third-party tracking cookies, so there is nothing optional to opt into or out of.'),
      p('Managing Cookies', 'You can clear or block cookies in your browser settings at any time; essential cookies simply keep the site working.'),
    ],
  },
  careers: {
    eyebrow: 'Company',
    title: 'Careers',
    description:
      'We are a security-first team building integrated cyber, IT, and software services across India and the UK. Every role we hire for is posted and managed through LinkedIn — head there to see what is open and apply.',
    sectionTag: 'How to Join Us',
    points: [
      p('We Hire Through LinkedIn', 'All of our openings are advertised on our LinkedIn page. It is the one place we keep current roles, requirements, and application details — always up to date.'),
      p('Why LinkedIn', 'Posting there lets us verify candidates properly — real profiles, real experience, and mutual connections — so we reach the right practitioners rather than sifting through anonymous inboxes.'),
      p('How to Apply', 'Find the role that fits on our LinkedIn page and apply directly there. Your LinkedIn profile and CV go straight to our hiring team.'),
      p('How We Hire', 'A clear, respectful, practitioner-led process — a conversation about your work, a hands-on exercise relevant to the role, and a straight answer either way.'),
    ],
    ctaTitle: 'Ready to apply?',
    ctaDesc: 'Our current openings live on LinkedIn — head there to find the right role and apply.',
    ctaButtonLabel: 'Contact Us',
    externalCta: LINKEDIN_JOBS_URL
      ? { label: 'View Openings on LinkedIn', href: LINKEDIN_JOBS_URL }
      : null,
  },
};
